"use client";

import Pagination from "@afa-shared/afa-components/dist/Pagination";
import Grid from "@components/client/Grid";
import clsx from "clsx";
import { parseAsInteger, useQueryState } from "nuqs";
import { RefObject, useCallback } from "react";

import { IFocusState, useFocusState } from "hooks/zustand/useFocusState";

import paginationStyles from "./Pagination.module.css";
import getPagination from "./dottedPaginationHelper";

interface PaginationProps {
  navbarLabel: string;
  itemsPerPage?: number;
  totalItems: number;
  listRef: RefObject<any>;
  searchParameters?: Record<string, string | number | boolean | null | undefined>;
  marginTop?: string;
  className?: string;
}

const PaginationBlock = ({
  itemsPerPage = 10,
  totalItems,
  searchParameters = {},
  marginTop,
  listRef,
  navbarLabel,
  className,
}: PaginationProps) => {
  const setOverridePageTopFocus = useFocusState(
    (state: IFocusState) => state.setOverridePageTopFocus,
  );
  const setOnPageLoadRef = useFocusState((state: IFocusState) => state.setOnPageLoadRef);

  // Manage ?page query parameter using nuqs

  const [page] = useQueryState("page", parseAsInteger.withDefault(1));

  const totalPaginationPages = Math.ceil(totalItems / itemsPerPage);

  // Build a query string for other filters/search parameters
  const queryString = Object.entries(searchParameters)
    .flatMap(([key, value]) =>
      value === undefined || value === null || key === "page" ? [] : `${key}=${value}`,
    )
    .join("&");

  const handleItemClick = useCallback(() => {
    setOverridePageTopFocus(true);
    setOnPageLoadRef(listRef);
  }, [setOverridePageTopFocus, setOnPageLoadRef, listRef]);

  // Generate pagination items with correct URLs and callbacks
  const paginationItems = getPagination(
    totalPaginationPages,
    page,
    "", // No slug prefix, URLs handled via query params
    queryString,
    handleItemClick,
  );

  return (
    totalPaginationPages > 0 && (
      <Grid
        margin={marginTop && `${marginTop} 0 0`}
        direction="row"
        justifyContent="center"
        className={clsx(paginationStyles.pagination, className)}
        data-testid="pagination"
      >
        <Pagination paginationItems={paginationItems} navbarLabel={navbarLabel} />
      </Grid>
    )
  );
};

export default PaginationBlock;
